import { getCallsMetrics, getCallsMetricsExport } from '../../../api/calls_metrics';
import React, { useState, useEffect, useContext } from 'react';
import IsScrolling from 'react-is-scrolling';
import Context from '../../../utils/context';
import CompleteDateRangePicker from '../../ui_kit/inputs/complete_date_range_picker/index.jsx';
import ExportButton from '../../ui_kit/buttons/export_button/index.jsx';
import styles from './index.module.css';

function CallsDashboard({ isScrolling }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const context = useContext(Context);
  const currentAccount = context.auth.currentAccount();

  useEffect(() => {
    let cancelled = false;

    const applyData = (params) => {
      setLoading(true);
      getCallsMetrics(params, (responseData) => {
        if (cancelled) {
          return;
        }
        setData(responseData);
        setLoading(false);
      });
    };

    if (isInitialLoad) {
      setIsInitialLoad(false);
      applyData({});
    } else if (startDate && endDate) {
      applyData({
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
      });
    } else if (!startDate && !endDate) {
      applyData({});
    }

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const exportParams = {
    ...(startDate && { start_date: startDate.toISOString().split('T')[0] }),
    ...(endDate && { end_date: endDate.toISOString().split('T')[0] }),
  };

  if (loading || !data) {
    return (
      <div className='container marg-bottom-50'>
        <div className='row'>
          <h1 className={`${styles.header} col-lg-6`}>Calls</h1>
          <CompleteDateRangePicker
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            startDate={startDate}
            endDate={endDate}
            isScrolling={isScrolling}
            style={{ marginLeft: 'auto' }}
          />
        </div>
        <div className='row'>
          <div className='col-lg-12'>
            <div className='content_container'>
              <p style={{ textAlign: 'center', padding: '40px' }}>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showReportedEvents = data.reported_events?.reported > 0;

  return (
    <div className='container marg-bottom-50'>
      <div className='row'>
        <h1 className={`${styles.header} col-lg-6`}>Calls</h1>
        <CompleteDateRangePicker
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
          isScrolling={isScrolling}
          style={{ marginLeft: 'auto' }}
        />
      </div>
      <div className='row' style={{ marginBottom: '0px' }}>
        <ExportButton requestExport={getCallsMetricsExport} params={exportParams} />
      </div>

      <div className={`${styles.metrics_grid} ${styles.metrics_grid_2col}`}>
        <div className={styles.metric_card}>
          <p className={styles.metric_label}>Patients who requested a call</p>
          <p className={styles.metric_value}>{data.call_requests?.distinct_patients ?? 0}</p>
        </div>

        <div className={styles.metric_card}>
          <p className={styles.metric_label}>Calls completed</p>
          <p className={styles.metric_value}>{data.completed_calls?.count ?? 0}</p>
          <p className={styles.metric_sublabel}>{data.completed_calls?.period_label}</p>
          {data.completed_calls?.change != null && (
            <p className={styles.metric_sublabel}>
              {data.completed_calls.change > 0 ? '+' : ''}
              {data.completed_calls.change} vs. prior period
            </p>
          )}
        </div>
      </div>

      <div className={`${styles.metrics_grid} ${styles.metrics_grid_2col}`}>
        <div className={styles.metric_card}>
          <p className={styles.metric_label}>Ready for the next step</p>
          {data.ready_for_next_step?.percentage != null ? (
            <>
              <p className={styles.metric_value}>
                {data.ready_for_next_step.percentage}
                <span className={styles.metric_unit}>%</span>
              </p>
              <p className={styles.metric_sublabel}>
                {data.ready_for_next_step.numerator} of {data.ready_for_next_step.denominator}
              </p>
            </>
          ) : (
            <p className={styles.metric_value_muted}>No data available.</p>
          )}
        </div>

        {showReportedEvents && (
          <div className={styles.metric_card}>
            <p className={styles.metric_label}>Reportable Events</p>
            <p className={styles.metric_value}>{data.reported_events.reported}</p>
          </div>
        )}
      </div>

      <div className={`${styles.metrics_grid} ${styles.metrics_grid_fill}`}>
        <div className={styles.metric_card}>
          <p className={styles.metric_label}>Post-call satisfaction</p>
          {data.satisfaction?.average != null ? (
            <p className={styles.metric_value}>{data.satisfaction.average}/5</p>
          ) : (
            <p className={styles.metric_value_muted}>No data available.</p>
          )}
        </div>

        <div className={styles.metric_card}>
          <p className={styles.metric_label}>Average call length</p>
          {data.call_duration?.average_minutes != null ? (
            <p className={styles.metric_value}>
              {data.call_duration.average_minutes}
              <span className={styles.metric_unit}> min</span>
            </p>
          ) : (
            <p className={styles.metric_value_muted}>No data available.</p>
          )}
        </div>

        <div className={styles.metric_card}>
          <p className={styles.metric_label}>Surveys completed</p>
          <p className={styles.metric_value}>{data.surveys_completed?.count ?? 0}</p>
        </div>
      </div>
    </div>
  );
}

export default IsScrolling(CallsDashboard);
